// Unit conservation ledger (IMPLEMENTATION §2.2). Per material, in integer units:
//   mined + granted == inWorld + sold + burned + fired + consumedBuild
// inWorld counts every item that exists (floor, building contents, ghost deliveries, Crate counts) plus the
// output a running machine is making (`proc`: its units are exactly the consumed set's units — recipes
// conserve units) and excludes heat.
// `granted` covers the Cork Popper, debug spawns and v0.2 supply drops (§2.2). The cold-open practice rounds
// are a counter on the Gallery (`GalleryB.practice`), not items: they never enter the ledger, and a practice
// shot books nothing as `fired`.
import type { Int } from './core/types.ts';
import type { SimDefs } from './defs.ts';
import { famDef } from './entities.ts';
import { type FamId, MATS, type Mat } from './gen/ids.ts';
import type { Building, ItemRef, Ledger, MatLedger, Proc, SimState } from './state.ts';

export type LedgerField = keyof MatLedger;

export function emptyLedger(): Ledger {
  const row = (): MatLedger => ({
    mined: 0,
    granted: 0,
    sold: 0,
    burned: 0,
    fired: 0,
    consumedBuild: 0,
  });
  return { lead: row(), coal: row(), copper: row(), iron: row() };
}

/** Adds (sign 1) or removes (sign −1) the units of `n` items fam T`tier` to/from a ledger field. */
export function book(
  s: SimState,
  d: SimDefs,
  fam: FamId,
  tier: Int,
  field: LedgerField,
  sign: 1 | -1,
  n: Int = 1,
): void {
  const per = d.tierUnits[tier - 1];
  if (per === undefined) throw new RangeError(`no tier ${tier}`);
  for (const u of famDef(d, fam).units) s.ledger[u.mat][field] += sign * n * u.n * per;
}

function zeroMats(): Record<Mat, Int> {
  return { lead: 0, coal: 0, copper: 0, iron: 0 };
}

function addUnits(acc: Record<Mat, Int>, d: SimDefs, fam: FamId, tier: Int, n: Int): void {
  const per = d.tierUnits[tier - 1] ?? 0;
  for (const u of famDef(d, fam).units) acc[u.mat] += n * u.n * per;
}

function addRefs(acc: Record<Mat, Int>, d: SimDefs, xs: readonly ItemRef[]): void {
  for (const it of xs) addUnits(acc, d, it.fam, it.tier, 1);
}

/**
 * Calls `fn` for every item a building holds as an ItemRef (ghost deliveries, chute carry, processor slots
 * and `out`, Packer `buf`/`out`, Gallery gun and queue, Dock queue). Crate contents are counts: see
 * `b.counts`. The one walker shared by `inWorld` and `checkInvariants`, so a new container is added once.
 */
export function forEachStoredRef(b: Building, fn: (r: ItemRef, where: string) => void): void {
  if (b.ghost) for (const r of b.ghost.delivered) fn(r, 'ghost');
  switch (b.type) {
    case 'chute':
      if (b.carry) fn(b.carry, 'carry');
      break;
    case 'caster':
    case 'grinder':
    case 'smelter':
    case 'press':
    case 'loader':
    case 'gunsmith':
      for (let i = 0; i < b.slots.length; i++) {
        for (const r of b.slots[i] ?? []) fn(r, `slot ${i}`);
      }
      for (const r of b.out) fn(r, 'out');
      break;
    case 'packer':
      for (const r of b.buf) fn(r, 'buf');
      for (const r of b.out) fn(r, 'out');
      break;
    case 'gallery':
      if (b.gun) fn(b.gun, 'gun');
      for (const r of b.queue) fn(r, 'queue');
      break;
    case 'dock':
      for (const r of b.queue) fn(r, 'queue');
      break;
    default:
      break;
  }
}

/** The running cycle's output of a processor (null for anything else / idle). */
export function procOf(b: Building): Proc | null {
  switch (b.type) {
    case 'caster':
    case 'grinder':
    case 'smelter':
    case 'press':
    case 'loader':
    case 'gunsmith':
      return b.proc;
    default:
      return null;
  }
}

/** Units of every material that currently exist as items (the heat tank is not an item). */
export function inWorld(s: SimState, d: SimDefs): Record<Mat, Int> {
  const acc = zeroMats();
  addRefs(acc, d, s.floor);
  const add = (r: ItemRef): void => addUnits(acc, d, r.fam, r.tier, 1);
  for (const b of s.buildings) {
    forEachStoredRef(b, add);
    if (b.type === 'crate' && b.fam !== null) {
      for (let t = 0; t < b.counts.length; t++) addUnits(acc, d, b.fam, t + 1, b.counts[t] ?? 0);
    }
    const p = procOf(b);
    if (p !== null) addUnits(acc, d, p.fam, p.tier, 1);
  }
  return acc;
}

/** Human-readable conservation violations (empty = balanced). */
export function conservationErrors(s: SimState, d: SimDefs): string[] {
  const world = inWorld(s, d);
  const out: string[] = [];
  for (const m of MATS) {
    const l = s.ledger[m];
    const lhs = l.mined + l.granted;
    const rhs = world[m] + l.sold + l.burned + l.fired + l.consumedBuild;
    if (lhs !== rhs) {
      out.push(
        `${m}: mined ${l.mined} + granted ${l.granted} = ${lhs} != inWorld ${world[m]} + sold ${l.sold} + burned ${l.burned} + fired ${l.fired} + consumedBuild ${l.consumedBuild} = ${rhs}`,
      );
    }
    for (const f of ['mined', 'granted', 'sold', 'burned', 'fired', 'consumedBuild'] as const) {
      if (l[f] < 0) out.push(`${m}.${f} is negative (${l[f]})`);
    }
  }
  return out;
}
